import type {
  EGlobalSearchTab,
  IChatListObject,
  ICrossChatSearchHit,
  IGlobalSearchGroup,
  IGlobalSearchItem,
  IGlobalSearchTabDefinition,
} from "@frontend-shared/types";
import type {
  KeyboardEvent,
  PointerEvent,
  ReactNode,
  RefCallback,
  RefObject,
} from "react";

// NOTE: NSAI-6853 — what a category tab shows in place of results, when it
// has none to show.
export enum EGlobalSearchTabStatus {
  // NOTE: Results (or the chat sections, which carry their own states).
  NONE = "none",
  // NOTE: Empty box on a tab that only lists once the user types.
  IDLE = "idle",
  MIN_LENGTH = "min_length",
  EMPTY = "empty",
}

export interface IGlobalSearchProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export interface IGlobalSearchContentProps {
  onClose: () => void;
}

export interface IUseGlobalSearchGateResolverProps {
  // NOTE: False while the palette is closed — the organization queries behind
  // the ORG_* gates are only worth making once somebody is searching.
  isEnabled: boolean;
}

export interface IUseGlobalSearchOrganizationInputsProps {
  isEnabled: boolean;
}

export interface IUseGlobalSearchFlagInputsProps {
  // NOTE: Set only for an organization the caller owns — the one flag read
  // from an organization endpoint (per-model request limits) is owner-only.
  ownedOrganizationUuid: string | undefined;
}

export interface IUseGlobalSearchClientGroupsProps {
  // NOTE: The live input — client groups are computed on the device, so they
  // follow every keystroke without waiting for the debounce.
  query: string;
  isEnabled: boolean;
}

export interface IUseGlobalSearchServerGroupsProps {
  debouncedQuery: string;
}

export interface IGlobalSearchServerGroupsResult {
  groups: IGlobalSearchGroup[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

export interface IUseGlobalSearchChatsProps {
  query: string;
  debouncedQuery: string;
  onClose: () => void;
}

export interface IUseGlobalSearchHighlightProps {
  query: string;
  // NOTE: Switching tab hands the highlight back to the first row, like a
  // new query does.
  tab: EGlobalSearchTab;
  // NOTE: Every source of rows, so the highlight is re-checked whenever one
  // of them lands.
  leadingGroups: readonly IGlobalSearchGroup[];
  trailingGroups: readonly IGlobalSearchGroup[];
  chats: readonly IChatListObject[];
  semanticHits: readonly ICrossChatSearchHit[] | null;
}

export interface IGlobalSearchHighlight {
  rootRef: RefObject<HTMLDivElement>;
  value: string;
  onValueChange: (value: string) => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onPointerMoveCapture: (event: PointerEvent<HTMLDivElement>) => void;
}

export interface IUseGlobalSearchFocusGuardProps {
  rootRef: RefObject<HTMLDivElement>;
}

export interface IGlobalSearchChatsState {
  chats: IChatListObject[];
  semanticHits: ICrossChatSearchHit[] | null;
  isTitleLoading: boolean;
  isTitleError: boolean;
  isSemanticLoading: boolean;
  isSemanticError: boolean;
  hasSemanticSection: boolean;
  isQueryBelowMinLength: boolean;
  isFetchingNextPage: boolean;
  loadMoreRef: RefCallback<HTMLDivElement>;
  setScrollContainer: (element: HTMLDivElement | null) => void;
  onRetry: () => void;
  onSelectChat: (chat: IChatListObject) => void;
  onSelectHit: (hit: ICrossChatSearchHit) => void;
}

export interface IGlobalSearchChatsSectionProps {
  state: IGlobalSearchChatsState;
  // NOTE: With a query, chats are one group among many and are capped like
  // them; the empty state only speaks for the whole palette when nothing else
  // matched either.
  isSearching: boolean;
  hasOtherResults: boolean;
}

export interface IGlobalSearchGroupProps {
  group: IGlobalSearchGroup;
  onSelectItem: (item: IGlobalSearchItem) => void;
}

export interface IGlobalSearchItemLogoProps {
  item: IGlobalSearchItem;
}

export interface IGlobalSearchItemRowProps {
  item: IGlobalSearchItem;
  onSelectItem: (item: IGlobalSearchItem) => void;
}

export interface IGlobalSearchGroupListProps {
  groups: readonly IGlobalSearchGroup[];
  onSelectItem: (item: IGlobalSearchItem) => void;
}

export interface IGlobalSearchServerStatusProps {
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

export interface IGlobalSearchHeaderProps {
  title: string;
  description: string;
  closeLabel: string;
  // NOTE: The search input, laid out between the icon and the close button.
  children: ReactNode;
}

export interface IUseGlobalSearchTabsResult {
  tabs: IGlobalSearchTabDefinition[];
  activeTab: EGlobalSearchTab;
  onSelectTab: (tab: EGlobalSearchTab) => void;
  onSelectAdjacentTab: (step: 1 | -1) => void;
}

export interface IUseGlobalSearchTabKeysProps {
  onSelectAdjacentTab: (step: 1 | -1) => void;
}

export interface IUseGlobalSearchBrowseGroupsProps {
  tab: EGlobalSearchTab;
  isEnabled: boolean;
}

export interface IGlobalSearchTabsProps {
  tabs: readonly IGlobalSearchTabDefinition[];
  activeTab: EGlobalSearchTab;
  onSelectTab: (tab: EGlobalSearchTab) => void;
  onSelectAdjacentTab: (step: 1 | -1) => void;
}

export interface IGlobalSearchTabButtonProps {
  definition: IGlobalSearchTabDefinition;
  isActive: boolean;
  onSelectTab: (tab: EGlobalSearchTab) => void;
}

export interface IGlobalSearchTabMessageProps {
  status: EGlobalSearchTabStatus;
  tab: EGlobalSearchTab;
  query: string;
}

export interface IGlobalSearchKbdProps {
  children: ReactNode;
  className?: string;
}

export interface IGlobalSearchTriggerProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}
